import { projectApi, type Project } from '@shared/api-client';
import { ProjectAvatar } from '@/components/project-avatar';
import { useQuery } from '@tanstack/react-query';
import { Check, ChevronsUpDown, LayoutGrid, Pin, Plus } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { usePinnedProjects } from '@/hooks/use-pinned-projects';
import { partitionPinnedProjects } from '@/lib/pinned-projects';

export const ProjectSwitcher = ({
  currentProjectId,
}: {
  currentProjectId: string | undefined;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { pinned } = usePinnedProjects();
  const projectsQuery = useQuery({
    queryKey: ['projects'],
    queryFn: projectApi.listProjects,
  });

  const projects = projectsQuery.data?.projects ?? [];
  const projectGroups = partitionPinnedProjects(projects, pinned);
  const currentProject = projects.find(
    (project) => project.id === currentProjectId,
  );
  const projectSection = location.pathname.endsWith('/realtime')
    ? '/realtime'
    : location.pathname.endsWith('/reports')
      ? '/reports'
      : location.pathname.endsWith('/data-sources')
        ? '/data-sources'
        : location.pathname.endsWith('/details')
          ? '/details'
          : '';

  const openProject = (projectId: string) => {
    navigate(`/projects/${encodeURIComponent(projectId)}${projectSection}`);
  };
  const projectItem = (project: Project) => (
    <DropdownMenuItem
      key={project.id}
      className="py-2"
      onSelect={() => openProject(project.id)}
    >
      <ProjectAvatar
        name={project.name}
        logoUrl={project.profile?.logoUrl}
        className="size-7"
      />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">
          {project.name}
        </span>
        <span className="block truncate text-xs text-ink-subtle">
          {project.primaryDomain ?? 'No primary domain'}
        </span>
      </span>
      <Check
        className={
          project.id === currentProjectId ? 'text-accent' : 'text-transparent'
        }
      />
    </DropdownMenuItem>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="flex min-w-0 max-w-[min(24rem,calc(100vw-8rem))] items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-surface-muted"
        aria-label="Switch project"
      >
        <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-surface-muted text-xs font-semibold text-ink-muted">
          {currentProject ? (
            <ProjectAvatar
              name={currentProject.name}
              logoUrl={currentProject.profile?.logoUrl}
              className="size-8"
            />
          ) : (
            <LayoutGrid className="size-4" />
          )}
        </span>

        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold text-ink">
            {currentProject?.name ?? 'All projects'}
          </span>
          <span className="block truncate text-xs text-ink-subtle">
            {currentProject?.primaryDomain ??
              (projectsQuery.isLoading
                ? 'Loading projects…'
                : `${projects.length} ${projects.length === 1 ? 'project' : 'projects'}`)}
          </span>
        </span>

        <ChevronsUpDown className="ml-1 size-4 shrink-0 text-ink-subtle" />
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="start"
        className="w-80 max-w-[calc(100vw-2rem)]"
      >
        <DropdownMenuLabel>Switch project</DropdownMenuLabel>

        <DropdownMenuItem className="py-2" onSelect={() => navigate('/')}>
          <span className="flex size-7 items-center justify-center rounded-md bg-surface-muted">
            <LayoutGrid className="size-3.5" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-medium">All projects</span>
            <span className="block text-xs text-ink-subtle">
              Portfolio overview
            </span>
          </span>
          <Check
            className={currentProjectId ? 'text-transparent' : 'text-accent'}
          />
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <div className="max-h-72 overflow-x-hidden overflow-y-auto">
          {projectGroups.pinned.length > 0 ? (
            <>
              <DropdownMenuLabel className="flex items-center gap-1.5 text-xs text-ink-subtle">
                <Pin className="size-3" fill="currentColor" /> Pinned
              </DropdownMenuLabel>
              {projectGroups.pinned.map(projectItem)}
            </>
          ) : null}
          {projectGroups.pinned.length > 0 &&
          projectGroups.others.length > 0 ? (
            <DropdownMenuSeparator className="mx-0" />
          ) : null}
          {projectGroups.pinned.length > 0 &&
          projectGroups.others.length > 0 ? (
            <DropdownMenuLabel className="text-xs text-ink-subtle">
              Other projects
            </DropdownMenuLabel>
          ) : null}
          {projectGroups.others.map(projectItem)}
        </div>

        {projectsQuery.isError ? (
          <p className="px-2 py-2 text-xs text-critical">
            Projects could not be loaded.
          </p>
        ) : null}

        <DropdownMenuSeparator />

        <DropdownMenuItem onSelect={() => navigate('/projects/new')}>
          <Plus />
          Add project
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
